import { backupTimesOf, backupTimesWhen, callbackWhen } from "@/lib/callbacks";
import { DIALER_ABANDONED_LABEL } from "@/lib/call-outcome";
import type { ContactChannel, LeadCallback, LeadCard, LeadCardCall, LeadCardCampaign } from "@/lib/types";
import { formatDateTime, formatDuration } from "@/lib/utils";

/**
 * The lead's card on the rep's phone (ADR-024): the words for what `GET /leads/{id}` says. Pure, for
 * `test/lead-card.test.ts`.
 */

/** The time where the lead is, as a rep would say it: "2:14 PM". Null without a timezone this
 *  browser knows. */
export function localTime(timezone: string | null, now: number): string | null {
  if (!timezone) return null;
  try {
    const text = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      hour: "numeric",
      minute: "2-digit",
    }).format(now);
    // Newer ICU puts a narrow no-break space before "PM"; the rest of the phone uses a plain one.
    return text.replaceAll(" ", " ");
  } catch {
    return null;
  }
}

const LANGUAGES: Record<string, string> = { en: "English", es: "Spanish" };

/** "es" -> "Spanish". A code the console has no word for is shown as the code. */
export function languageName(code: string | null): string | null {
  if (!code) return null;
  return LANGUAGES[code.toLowerCase()] ?? code.toUpperCase();
}

// --- the communication preference (ACL-343) --------------------------------------------------------
//
// A preference on the lead, not an outcome: no stage or disposition sets or clears it, and anyone who
// may edit the lead changes it either way, a rep included. Text first holds for 24 hours from when it
// was set (`text_first_until`, a lease): once that has passed the lead reads as calls and texts, even
// before core-api writes the switch back. The server decides; these rules only mirror it.

export type ChannelFields = { contact_channel?: ContactChannel | null; text_first_until?: string | null };

export const CONTACT_CHANNELS: readonly { value: ContactChannel; label: string; hint: string }[] = [
  { value: "calls_and_sms", label: "Calls and texts", hint: "Called and texted as normal." },
  {
    value: "text_first",
    label: "Text first",
    hint: "No automatic calls for 24 hours from now, then back to calls and texts on its own.",
  },
  { value: "sms_only", label: "SMS only", hint: "Never called by the dialer: deaf, or asked not to be called." },
];

/** The lead's preference now. A missing one reads as calls and texts, and so does a Text first whose
 *  24 hours are over. */
export function contactChannelOf(lead: ChannelFields, now: number = Date.now()): ContactChannel {
  if (lead.contact_channel === "sms_only") return "sms_only";
  if (lead.contact_channel === "text_first") {
    const until = lead.text_first_until ? Date.parse(lead.text_first_until) : Number.NaN;
    return Number.isNaN(until) || until > now ? "text_first" : "calls_and_sms";
  }
  return "calls_and_sms";
}

/** True only for a lead the dialer never calls. */
export function isSmsOnly(lead: ChannelFields): boolean {
  return lead.contact_channel === "sms_only";
}

/** "sms_only" -> "SMS only". */
export function contactChannelLabel(channel: ContactChannel): string {
  return CONTACT_CHANNELS.find((c) => c.value === channel)?.label ?? channel;
}

/** When a Text first lead's automatic calls start, in the reader's own clock; null for any other
 *  preference, or a Text first that is already over. */
export function textFirstEnds(lead: ChannelFields, now: number = Date.now()): string | null {
  return contactChannelOf(lead, now) === "text_first" && lead.text_first_until
    ? formatDateTime(lead.text_first_until)
    : null;
}

/** The badge for the lead's preference, or null for calls and texts. */
export function contactChannelBadge(
  lead: ChannelFields,
  now: number = Date.now(),
): { text: string; title: string } | null {
  const channel = contactChannelOf(lead, now);
  if (channel === "sms_only") return { text: "SMS only", title: "Asked to be texted: the dialer never calls this lead" };
  if (channel === "text_first") {
    const ends = textFirstEnds(lead, now);
    return { text: "Text first", title: ends ? `No automatic calls until ${ends}` : "No automatic calls for now" };
  }
  return null;
}

/** The line shown with the lead on the phone: what they asked for, in a rep's words; null for calls and
 *  texts. */
export function contactChannelLine(lead: ChannelFields, now: number = Date.now()): string | null {
  const channel = contactChannelOf(lead, now);
  if (channel === "sms_only") return "SMS only: asked to be texted, not called";
  if (channel === "text_first") {
    const ends = textFirstEnds(lead, now);
    return ends ? `Text first: no automatic calls until ${ends}` : "Text first: no automatic calls for now";
  }
  return null;
}

/** What an edit sends for the preference: nothing when the API does not serve the field yet (core-api
 *  refuses a field it does not know), and nothing when the pick is the lead's current one. Picking
 *  Text first again once it is over sends it, which starts a new 24 hours. */
export function contactChannelInput(
  lead: ChannelFields,
  picked: ContactChannel,
  now: number = Date.now(),
): ContactChannel | undefined {
  if (lead.contact_channel === undefined) return undefined;
  return picked === contactChannelOf(lead, now) ? undefined : picked;
}

/**
 * The open callback in the card's one line (ADR-080): when, what for, and who it is kept for. While
 * the dialer still holds its first call, the backup times come after its own (ACL-559, ADR-183 and
 * ADR-210 in `platform`): "or else" is all the rep on the call needs. The callback the system booked from a
 * backup is called that.
 */
export function cardCallbackText(
  cb: Pick<LeadCallback, "due_at" | "status" | "purpose" | "rep"> &
    Partial<Pick<LeadCallback, "backup_at" | "backup_times" | "backup_state" | "backup_of">>,
  leadZone: string | null,
  viewerZone?: string,
): string {
  const head = `${cb.backup_of ? "Backup callback" : "Callback"} ${cb.status === "due" ? "due" : "set for"}`;
  const times = backupTimesOf(cb);
  const orElse =
    times.length > 0 && cb.backup_state === "waiting" && cb.status === "upcoming"
      ? `or else ${backupTimesWhen(times, leadZone, Date.parse(cb.due_at), viewerZone)}`
      : null;
  const when = callbackWhen(cb.due_at, leadZone, viewerZone).there;
  return [`${head} ${when}`, orElse, cb.purpose, cb.rep && `kept for ${cb.rep}`].filter(Boolean).join(" · ");
}

/** A slug in the rep's words: "contract_review" -> "Contract review". */
export function humanize(slug: string | null): string | null {
  const text = slug?.replaceAll("_", " ").trim();
  return text ? text[0]!.toUpperCase() + text.slice(1) : null;
}

/** The lead on the list of the campaign the call came through, else the list they were last called
 *  from. */
export function listFor(
  card: Pick<LeadCard, "campaigns">,
  campaignId: string | null,
): LeadCardCampaign | null {
  const here = card.campaigns.find((c) => c.campaign_id === campaignId);
  if (here) return here;
  const latest = [...card.campaigns].sort((a, b) =>
    (b.last_attempt_at ?? "").localeCompare(a.last_attempt_at ?? ""),
  );
  return latest[0] ?? null;
}

/** How a stay in a line ended without a conversation, by the event-tracker's outcome. */
const UNANSWERED: Record<string, string> = {
  abandoned: "Hung up while waiting",
  drop: "Hung up as it connected",
  timeout: "Nobody was free",
};

/** Whether the call was a conversation, timed: a stay in a line or a call only the switch recorded
 *  that a rep talked on. A dial never is — one that reached a rep is listed as its stay. */
function talked(call: LeadCardCall): boolean {
  return (call.via === "queue" || call.via === "direct") && Boolean(call.talk_seconds);
}

/**
 * One of the lead's calls, in a line: "Talked 3:32 with Marcus Lee · Callback", "Hung up while
 * waiting", "No answer · power dial", "Missed · direct call". `label` turns an outcome's slug into
 * the campaign's word for it; a slug it does not know is shown in plain words. `withOutcome: false`
 * leaves the outcome to a tag of its own beside the words (`OutcomeTag`).
 */
export function callText(
  call: LeadCardCall,
  label: (slug: string) => string | undefined,
  { withOutcome = true }: { withOutcome?: boolean } = {},
): string {
  let text: string;
  if (talked(call)) {
    text = `Talked ${formatDuration(Math.round(call.talk_seconds!))}`;
    if (call.rep_name) text += ` with ${call.rep_name}`;
  } else if (call.abandoned) {
    // The lead answered and the dialer hung up on them: they did not hang up while waiting.
    text = DIALER_ABANDONED_LABEL;
  } else if (call.via === "queue") {
    // The event-tracker writes a stay's outcome in capitals: CONNECTED, ABANDONED, REHOMED.
    const outcome = call.outcome?.toLowerCase() ?? "";
    text = UNANSWERED[outcome] ?? humanize(outcome) ?? "Did not connect";
  } else if (call.via === "direct") {
    // core-api's words for how it ended (`src/direct_calls.py` in `platform`): missed, no_answer,
    // busy, failed.
    text = `${humanize(call.outcome) ?? "Did not connect"} · direct call`;
  } else {
    text = humanize(call.outcome) ?? "Dialed";
    if (call.kind) text += ` · ${call.kind} dial`;
  }
  if (call.disposition && withOutcome) text += ` · ${outcomeText(call.disposition, label)}`;
  return text;
}

/** A recorded outcome's words: the campaign's label, or the slug in plain words. */
export function outcomeText(slug: string, label: (slug: string) => string | undefined): string {
  return label(slug) ?? humanize(slug) ?? slug;
}

/** How one of the lead's calls went, as the card marks it: the row's icon and its tone. */
export type CallResult = "talked" | "voicemail" | "no_answer" | "busy" | "failed" | "missed";

/** One of the lead's calls as a row of the card: what happened, with whom or how, and the outcome
 *  recorded for it. The same facts as `callText`, kept apart so the card can draw them. */
export interface CallLine {
  result: CallResult;
  /** What happened: "Talked 3:32", "Voicemail", "No answer", "Hung up while waiting". */
  title: string;
  /** Who it was with, or how it was placed: "with Marcus Lee", "Power dial". */
  detail: string | null;
  /** The outcome recorded for it, in the campaign's word. Null when there is none, and when the
   *  title has said it: a voicemail recorded as Voicemail is said once. */
  outcome: string | null;
}

/** How a dial that reached nobody ended, as a mark. Anything the switch says that is not one of
 *  these (a cancel, an abandon, nothing at all) is a call nobody answered. */
const DIAL_RESULTS: Record<string, CallResult> = {
  voicemail: "voicemail",
  busy: "busy",
  failed: "failed",
};

/** How a call only the switch recorded that nobody talked on is marked. */
const DIRECT_RESULTS: Record<string, CallResult> = {
  missed: "missed",
  busy: "busy",
  failed: "failed",
};

/**
 * One of the lead's calls for the card's list of earlier calls. A conversation is "Talked" with
 * its length and the rep; a stay in a line nobody took is what became of the caller; a dial that
 * reached nobody is how it ended and how it was placed; a call only the switch recorded is how it
 * ended, placed directly. `label` turns an outcome's slug into the campaign's word for it; a slug
 * it does not know is shown in plain words.
 */
export function callLine(call: LeadCardCall, label: (slug: string) => string | undefined): CallLine {
  const recorded = call.disposition ? (label(call.disposition) ?? humanize(call.disposition)) : null;
  const line = (result: CallResult, title: string, detail: string | null): CallLine => ({
    result,
    title,
    detail,
    outcome: recorded !== null && recorded.toLowerCase() !== title.toLowerCase() ? recorded : null,
  });
  if (talked(call)) {
    return line(
      "talked",
      `Talked ${formatDuration(Math.round(call.talk_seconds!))}`,
      call.rep_name ? `with ${call.rep_name}` : null,
    );
  }
  if (call.abandoned) {
    // The lead answered and the dialer hung up on them: they did not hang up while waiting.
    return line("missed", DIALER_ABANDONED_LABEL, "No rep took it in time");
  }
  if (call.via === "queue") {
    const outcome = call.outcome?.toLowerCase() ?? "";
    return line("missed", UNANSWERED[outcome] ?? humanize(outcome) ?? "Did not connect", null);
  }
  if (call.via === "direct") {
    const outcome = call.outcome?.toLowerCase() ?? "";
    return line(
      DIRECT_RESULTS[outcome] ?? "no_answer",
      humanize(call.outcome) ?? "Did not connect",
      "Direct call",
    );
  }
  const outcome = call.outcome?.toLowerCase() ?? "";
  return line(
    DIAL_RESULTS[outcome] ?? "no_answer",
    humanize(call.outcome) ?? "Dialed",
    call.kind ? `${humanize(call.kind)} dial` : null,
  );
}

/** What the phone says about an inbound caller who has no card (ACL-284). */
export interface CallerRecordText {
  kind: "no_record" | "withheld" | "lookup_failed";
  tone: "neutral" | "warning";
  title: string;
  /** A word or two for the corner of the card. */
  badge: string;
  detail: string;
}

/**
 * The words for an inbound caller the ACD's lookup did not find, by why: we have no record of the
 * number, there was no number to look up, or the lookup could not answer. The last is not "unknown
 * caller": they may well have a record. Null when there is nothing to say (found, or nobody asked).
 */
export function callerRecordText(status: string | null): CallerRecordText | null {
  switch (status) {
    case "no_lead":
      return {
        kind: "no_record",
        tone: "neutral",
        title: "No existing record found for this caller",
        badge: "Not in CRM",
        detail: "Nobody in the CRM has this number.",
      };
    case "unreadable_number":
      return {
        kind: "withheld",
        tone: "neutral",
        title: "Caller ID withheld",
        badge: "No number",
        detail: "The caller hid their number, so there is nothing to look up in the CRM.",
      };
    case "lookup_failed":
      return {
        kind: "lookup_failed",
        tone: "warning",
        title: "Couldn't check the CRM",
        badge: "Lookup failed",
        detail: "This caller may still have a record.",
      };
    default:
      return null;
  }
}

/**
 * What the timeline says of a message a call left on a voicemail (ADR-128 in `platform`): that it
 * was left, or that it did not play, and how it was said. A greeting written as text was spoken, by
 * a voice the timeline names; a recording has no words here.
 */
export function voicemailLeftText(voicemail: {
  name: string;
  words: string | null;
  version?: string | null;
  voice: string | null;
  voice_own?: boolean;
  result: string | null;
}): { title: string; by: string; failed: boolean } {
  const failed = voicemail.result === "failed";
  // A greeting with two texts says which one this call was given (ADR-116 in `platform`).
  const version = voicemail.version === "a" || voicemail.version === "b" ? `version ${voicemail.version.toUpperCase()} · ` : "";
  return {
    title: failed ? `Voicemail “${voicemail.name}” did not play` : `Left voicemail “${voicemail.name}”`,
    by:
      voicemail.words === null
        ? "a recording"
        : voicemail.voice
          ? // A rep's own voice (ADR-117) is named as theirs, not as a voice called by their name.
            voicemail.voice_own
            ? `${version}spoken in ${voicemail.voice}'s own voice`
            : `${version}spoken by ${voicemail.voice}`
          : `${version}spoken`,
    failed,
  };
}
